/**
 * Check if a string is a valid http(s) URL.
 * Uses strict protocol equality to prevent matching schemes like `httpfoo:`.
 */
export const isUrl = (text: string): boolean => {
  return isSafeHttpUrl(text);
};

/**
 * Returns true only when the URL has exactly `http:` or `https:` as its protocol.
 * Rejects `javascript:`, `data:`, `blob:`, `httpfoo:`, malformed strings, and empty input.
 */
export function isSafeHttpUrl(url: string | undefined | null): url is string {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Opens a URL in a new browser tab only when it passes the http(s) allowlist.
 * Always adds `noopener,noreferrer` for `_blank` targets.
 * Returns `true` if the URL was opened, `false` if it was rejected.
 */
export function openExternalUrl(
  url: string | undefined | null,
  target = '_blank',
  features = 'noopener,noreferrer',
): boolean {
  if (!isSafeHttpUrl(url)) return false;
  window.open(
    url,
    target,
    target === '_blank' ? withNoopener(features) : features,
  );
  return true;
}

/** Ensures `noopener,noreferrer` are present regardless of what the caller passed. */
function withNoopener(features: string): string {
  const parts = new Set(
    features
      .split(',')
      .map((f) => f.trim())
      .filter(Boolean),
  );
  parts.add('noopener');
  parts.add('noreferrer');
  return [...parts].join(',');
}

/**
 * Returns true when `url` is safe to pass to `fetch()`: a same-origin absolute
 * path (e.g. `/proxy/workflow/...`), or an absolute `https:` URL whose host
 * isn't a private, loopback, or link-local IP literal.
 *
 * Use this before `fetch()`-ing a URL that came from a remote response (e.g. a
 * file's presigned/download URL) rather than a hardcoded literal — it blocks
 * the browser from being tricked into fetching internal-only resources such
 * as cloud metadata endpoints (`169.254.169.254`) or `localhost` services.
 * Protocol-relative URLs (`//host/path`) are rejected rather than treated as
 * same-origin, since browsers resolve them against whatever host is current.
 */
export function isSafeFetchUrl(url: string | undefined | null): url is string {
  if (!url) return false;
  if (isSameOriginPath(url)) return true;

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'https:') return false;
  return !isPrivateOrLoopbackHost(parsed.hostname);
}

/**
 * Before resolving a URL, browsers strip ASCII tab/CR/LF characters
 * outright and normalize `\` to `/` for special schemes. So `/\host`,
 * `/{tab}/host`, and `/{newline}/host` all behave like the network-path
 * reference `//host` even though none of them start with `//` literally —
 * replicate that normalization before testing the prefix, so nothing that
 * would parse as protocol-relative is mistaken for same-origin.
 */
function isSameOriginPath(url: string): boolean {
  const normalized = url.replace(/[\t\r\n]/g, '').replace(/\\/g, '/');
  return normalized.startsWith('/') && !normalized.startsWith('//');
}

function isPrivateOrLoopbackHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  if (host === 'localhost' || host === '0.0.0.0') return true;

  // `URL#hostname` wraps IPv6 literals in brackets (e.g. "[::1]") -- that's
  // the only reliable signal it's an IPv6 address rather than a DNS name
  // (which could otherwise legitimately start with "fc"/"fd"/"fe80").
  if (host.startsWith('[') && host.endsWith(']')) {
    const addr = host.slice(1, -1);
    if (addr === '::1' || addr === '::') return true; // loopback / unspecified
    if (addr.startsWith('fc') || addr.startsWith('fd')) return true; // fc00::/7 unique-local

    // fe80::/10 link-local: the first 16-bit group ranges 0xfe80-0xfebf.
    // String-prefix matching on "fe80:" would miss e.g. "fe90::1", since the
    // range constrains the group's *value*, not just its leading digits.
    const firstGroup = parseInt(addr.split(':')[0] || '', 16);
    if (firstGroup >= 0xfe80 && firstGroup <= 0xfebf) return true;

    if (addr.startsWith('::ffff:')) {
      // `URL` normalizes IPv4-mapped IPv6 to two hex groups (e.g.
      // "::ffff:127.0.0.1" becomes "::ffff:7f00:1"), never a dotted-quad
      // suffix -- decode those groups back into an IPv4 address to reuse
      // the check below. An unrecognized shape fails closed (rejected).
      const hexGroups = addr.slice('::ffff:'.length).split(':');
      if (
        hexGroups.length === 2 &&
        hexGroups.every((g) => /^[0-9a-f]{1,4}$/.test(g))
      ) {
        const high = parseInt(hexGroups[0], 16);
        const low = parseInt(hexGroups[1], 16);
        const mappedIpv4 = [
          (high >> 8) & 0xff,
          high & 0xff,
          (low >> 8) & 0xff,
          low & 0xff,
        ].join('.');
        return isPrivateOrLoopbackHost(mappedIpv4);
      }
      return true;
    }
    return false;
  }

  const ipv4 = host.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (!ipv4) return false; // Not an IP literal -- treat as a normal DNS hostname.

  const a = Number(ipv4[1]);
  const b = Number(ipv4[2]);
  if (a === 0) return true; // 0.0.0.0/8
  if (a === 10) return true; // 10.0.0.0/8
  if (a === 127) return true; // 127.0.0.0/8 (loopback)
  if (a === 169 && b === 254) return true; // 169.254.0.0/16 (incl. cloud metadata)
  if (a === 172 && b >= 16 && b <= 31) return true; // 172.16.0.0/12
  if (a === 192 && b === 168) return true; // 192.168.0.0/16
  return false;
}

/**
 * Navigates the current tab to `url` via `window.location.assign` only when the
 * URL passes the http(s) allowlist.
 * Returns `true` if the navigation was initiated, `false` if it was rejected.
 */
export function assignExternalUrl(url: string | undefined | null): boolean {
  if (!isSafeHttpUrl(url)) return false;
  window.location.assign(url);
  return true;
}

/** Schemes a link rendered from untrusted markdown may point at. */
const NAVIGABLE_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);

/**
 * Returns true when `href` is safe to render as a clickable anchor.
 *
 * Written for markdown produced by an LLM, where the link destination is not
 * trusted. Two shapes have to be refused:
 *
 * - **Blank.** react-markdown blanks a destination whose scheme it rejects, and
 *   `<a href="" target="_blank">` reopens the *current* page in a new tab,
 *   which reads to the user as a silent redirect.
 * - **Not ours.** A bare relative href resolves against the current route into
 *   an in-app URL that means nothing, and `//host` (or `/\host`, which the
 *   parser treats the same way) looks root-relative while actually pointing
 *   off-origin. Resolving and comparing origins settles both, where a prefix
 *   test only catches the spellings it enumerates.
 *
 * Deliberately narrower than `isSafeFetchUrl`, which exists for URLs we
 * `fetch()`: this allows `mailto:`, since a link the user clicks is not a
 * request we make. It is also narrower than react-markdown's own scheme
 * allowlist, which additionally permits `irc:`/`ircs:`/`xmpp:`.
 */
export function isNavigableHref(
  href: string | undefined | null,
): href is string {
  if (!href) return false;
  try {
    if (href.startsWith('/')) {
      const { origin } = new URL(href, window.location.origin);
      return origin === window.location.origin;
    }
    return NAVIGABLE_PROTOCOLS.has(new URL(href).protocol);
  } catch {
    // Relative reference (no base given), or unparseable.
    return false;
  }
}
